import './assets/main.css'
import '@/ui/tokens/index.css'
import './assets/animations.css'
import './assets/scrollbar.css'


import { ViteSSG } from 'vite-ssg'
import { createPinia } from 'pinia'
import PrimeVue from 'primevue/config'
import Tooltip from 'primevue/tooltip'
import FocusTrap from 'primevue/focustrap'

import App from './App.vue'
import { routes, scrollBehavior, registerRouterGuards, setActiveRouter } from '@/router'
import { MyPreset } from './themes/primevue-preset'
import { i18n } from '@/locales'
import { bindFairHead } from '@/seo/fairHead'
import { applyFairSeo } from '@/composables/useFairSeo'
import { getFairRouteSeo } from '@/seo/routeSeo'

import ClickOutside from '@/directives/ClickOutsideDirective'
import { vCopy } from '@/directives/vCopy'
import type { App as VueApp } from 'vue'

function registerDirectives(app: VueApp): void {
  app.directive('focustrap', FocusTrap)
  app.directive('tooltip', Tooltip)
  app.directive('click-outside', ClickOutside)
  app.directive('copy', vCopy)
}

async function registerClientOnly(): Promise<void> {
  const { register } = await import('swiper/element/bundle')
  register()

  if (!import.meta.env.DEV) {
    return
  }

  ;(window as any).openSurvey = async () => {
    const { useSurveyModal } = await import('@/features/survey/store')
    const surveyStore = useSurveyModal()
    await surveyStore.showSurveyModal()
    console.log('Survey modal opened! Use window.openSurvey() to open it again.')
  }
  console.log('💡 Development helper: Use window.openSurvey() to open the survey modal')

  ;(window as any).openTasteSurvey = async () => {
    const { useTasteClarificationModal } = await import('@/features/taste/store')
    const tasteStore = useTasteClarificationModal()
    await tasteStore.showTasteModal()
    console.log('Taste survey modal opened! Use window.openTasteSurvey() to open it again.')
  }
  console.log('💡 Development helper: Use window.openTasteSurvey() to open the taste survey modal')

  ;(window as any).openGiftSurvey = async () => {
    const { useGiftModal } = await import('@/features/gift/store')
    const giftStore = useGiftModal()
    await giftStore.showGiftModal()
    console.log('Gift survey modal opened! Use window.openGiftSurvey() to open it again.')
  }
  console.log('💡 Development helper: Use window.openGiftSurvey() to open the gift survey modal')

  ;(window as any).openLookSurvey = async () => {
    const { useLookModal } = await import('@/features/look/store')
    const lookStore = useLookModal()
    await lookStore.showLookModal()
    console.log('Look survey modal opened! Use window.openLookSurvey() to open it again.')
  }
  console.log('💡 Development helper: Use window.openLookSurvey() to open the look survey modal')
}

export const createApp = ViteSSG(
  App,
  {
    routes,
    scrollBehavior,
    base: import.meta.env.BASE_URL,
  },
  async ({ app, router, head, isClient }) => {
    if (head) {
      bindFairHead(head as never)
    }

    const pinia = createPinia()
    app.use(pinia)
    app.use(i18n)
    app.use(PrimeVue, {
      theme: {
        preset: MyPreset,
        options: {
          darkModeSelector: '.fake-dark-selector',
          cssLayer: {
            name: 'primevue',
            order: 'tailwind-base, primevue, tailwind-utilities',
          },
        },
      },
    })
    registerDirectives(app)

    setActiveRouter(router)
    registerRouterGuards(router)

    // Ensure Home SEO (incl. JSON-LD) is on the Unhead instance used by vite-ssg
    applyFairSeo(getFairRouteSeo('home', '/'))

    if (isClient) {
      await registerClientOnly()
      // lift the pre-hydration curtain (index.html) once the REAL route is
      // resolved — without this a hard refresh of any deep page flashes the
      // prerendered home markup for a second or two.
      // FE-003.3 — hand off to the branded overlay instead of leaving a gap:
      // the router guard has already started the loader by the time isReady
      // settles, so wait one painted frame for the overlay to be on screen
      // before uncovering #app.
      router.isReady().finally(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            document.documentElement.classList.remove('boot-pending')
          })
        })
      })
    }
  },
)
